import { revalidateLogic, useStore } from '@tanstack/react-form'
import { CircleHelp, Mars, Venus } from 'lucide-react'
import { z } from 'zod'

import { dateOfBirthSchema, patientSexSchema } from '@snootsvet/shared/portal'

import { useAppForm } from '@/customer/components/form'
import { FieldWrapper } from '@/customer/components/form/fields'
import { BreedAutocomplete } from '@/customer/components/pet/breed-autocomplete'
import { SpeciesIcon } from '@/customer/components/pet/species-icon'

const SPECIES_OPTIONS = [
  { value: 'dog', label: 'Dog', icon: <SpeciesIcon species="dog" className="size-10" /> },
  { value: 'cat', label: 'Cat', icon: <SpeciesIcon species="cat" className="size-10" /> },
] as const

const SEX_OPTIONS = [
  { value: 'male', label: 'Male', icon: <Mars className="size-8" />, color: 'baby-blue' },
  { value: 'female', label: 'Female', icon: <Venus className="size-8" />, color: 'pink' },
  {
    value: 'unknown',
    label: 'Not sure',
    icon: <CircleHelp className="size-8" />,
    color: 'neutral',
  },
] as const

/**
 * Zod schema for the pet-creation form. Exported so consumers can
 * derive types or preview validation shape, but the form itself owns
 * the wiring - most callers only care about the resulting values.
 */
export const petFormSchema = z.object({
  petName: z.string().min(1, "Please enter your pet's name"),
  dateOfBirth: z.string().min(1, 'Please pick a date of birth').pipe(dateOfBirthSchema),
  species: z.enum(['dog', 'cat'], "Please select your pet's species"),
  sex: z.enum([...patientSexSchema.options], "Please select your pet's sex"),
  breed: z.string().optional(),
})
export type PetFormValues = z.infer<typeof petFormSchema>

const EMPTY_VALUES = {
  petName: '',
  dateOfBirth: '',
  species: null,
  sex: null,
  breed: '',
} as unknown as PetFormValues

/**
 * Return value from the `PetForm` render prop - exposes just the
 * bits the surrounding layout needs to wire up its own navigation
 * (Back button, submit affordance, disabled state).
 *
 * We hand back an imperative `submit()` rather than a click handler so
 * either a form-level submit or an external "Next" button can trigger
 * validation + submission uniformly.
 */
export interface PetFormRenderState {
  submit: () => void
  canSubmit: boolean
  isSubmitting: boolean
}

interface PetFormProps {
  /**
   * Called with the validated + trimmed values on submit. Return a
   * promise; the form's `isSubmitting` flag is derived from it. Throw
   * to surface an error on the pet-name field (the endpoint has no
   * conflict responses under normal use, so field-level mapping isn't
   * meaningful today).
   */
  onSubmit: (values: PetFormValues) => Promise<void>
  /**
   * Render prop for whatever chrome the caller wants around the form -
   * portal pages use a plain button row, the register flow uses
   * `<RegistrationNav>`. Consumers get `submit`, `canSubmit`, and
   * `isSubmitting` for wiring their controls.
   */
  children: (state: PetFormRenderState) => React.ReactNode
}

/**
 * Layout-agnostic pet-details form. Same fields as the register-flow
 * step 2 page, extracted so the portal's "Add a pet" flow can reuse
 * the exact same inputs and validation. Rendering the surrounding
 * headline, progress indicator, and nav is the caller's job.
 */
export function PetForm({ onSubmit, children }: PetFormProps) {
  const form = useAppForm({
    defaultValues: EMPTY_VALUES,
    validationLogic: revalidateLogic({ mode: 'submit', modeAfterSubmission: 'change' }),
    validators: { onDynamic: petFormSchema },
    onSubmit: async ({ value, formApi }) => {
      try {
        await onSubmit({
          ...value,
          breed: value.breed?.trim() ? value.breed.trim() : undefined,
        })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Something went wrong.'
        formApi.setFieldMeta('petName', (meta) => ({ ...meta, errors: [message] }))
      }
    },
  })

  const canSubmit = useStore(form.store, (s) => s.canSubmit && !s.isSubmitting)
  const isSubmitting = useStore(form.store, (s) => s.isSubmitting)

  return (
    <div className="flex flex-col gap-8">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
        className="flex flex-col gap-8"
      >
        <form.AppForm>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <form.AppField name="petName">
              {(field) => (
                <field.InputField label="Pet Name" placeholder="e.g. Buster" autoComplete="off" />
              )}
            </form.AppField>
            <form.AppField name="dateOfBirth">
              {(field) => (
                <field.InputField
                  label="Date of Birth"
                  type="date"
                  description="Not sure? A rough guess is fine - we won't quiz you."
                />
              )}
            </form.AppField>
          </div>

          <form.AppField name="species">
            {(field) => <field.CardRadioGroupField label="Species" options={SPECIES_OPTIONS} />}
          </form.AppField>

          <form.AppField name="sex">
            {(field) => <field.CardRadioGroupField label="Sex" options={SEX_OPTIONS} />}
          </form.AppField>

          {/* Breed uses `BreedAutocomplete` (species-filtered
              suggestions from the shared breed lists) rather than
              the default text `InputField`. Free typing still saves,
              so unusual breeds aren't blocked. `form.Subscribe`
              pipes the current species through so the field toggles
              between disabled + filtered lists in lockstep with the
              species picker above. */}
          <form.Subscribe selector={(s) => s.values.species}>
            {(species) => (
              <form.Field name="breed">
                {(field) => (
                  <FieldWrapper
                    field={field}
                    label="Breed"
                    required={false}
                    render={() => (
                      <BreedAutocomplete
                        species={species}
                        value={field.state.value ?? ''}
                        onValueChange={(value) => field.handleChange(value)}
                        placeholder={species ? 'e.g. Golden Retriever' : 'Pick a species first'}
                        disabled={!species}
                      />
                    )}
                  />
                )}
              </form.Field>
            )}
          </form.Subscribe>
        </form.AppForm>
      </form>

      {children({ submit: () => form.handleSubmit(), canSubmit, isSubmitting })}
    </div>
  )
}
